import React, { FC, useMemo } from 'react';
import { ButtonProps } from 'antd';

import { Text, Title } from 'ui/components/Typography';
import { ModalButton } from './ModalButton';
import { BgEllipse } from './BgEllipse';

import ErrorCircleSvg from 'assets/img/errorCircle.svg';
import InfoCircleSvg from 'assets/img/infoCircle.svg';
import SuccessCircleSvg from 'assets/img/successCircle.svg';
import WhaleEmptySvg from 'assets/img/whaleEmpty.svg';
import WhaleFailSvg from 'assets/img/whaleFail.svg';
import WhaleProcessSvg from 'assets/img/whaleProcess.svg';
import WhaleSuccessSvg from 'assets/img/whaleSuccess.svg';

import { colors } from '@10d/tend-ui-tokens/samolet';

import { IModalConfirmProps } from '../types';

import { StyledConfirmModal } from './styled';

const getModalWidthByType = (
  type: IModalConfirmProps['modalType'] = 'info',
  size: IModalConfirmProps['size'] = 'small',
) => (size === 'small' ? (type?.startsWith('whale') ? 420 : 400) : 600);

export const ModalConfirm: FC<IModalConfirmProps> = ({
  modalType = 'info',
  title,
  subTitle,
  text,
  content,
  loading,
  cancelText = 'Отмена',
  okText = 'OK',
  size = 'small',
  close,
  onlyOkButton,
  ...props
}) => {
  const okButtonProps: ButtonProps = useMemo(() => {
    switch (modalType) {
      case 'error':
        return { loading, type: 'primary', danger: true };

      default:
        return { loading, type: 'primary' };
    }
  }, [loading, modalType]);

  const modalIconSrc = useMemo(() => {
    switch (modalType) {
      case 'info':
        return InfoCircleSvg;
      case 'success':
        return SuccessCircleSvg;
      case 'error':
        return ErrorCircleSvg;
      case 'whaleProcess':
        return WhaleProcessSvg;
      case 'whaleSuccess':
        return WhaleSuccessSvg;
      case 'whaleError':
        return WhaleFailSvg;
      case 'whaleEmpty':
        return WhaleEmptySvg;
    }
  }, [modalType]);

  const footer = useMemo(() => {
    const result = [];
    if (!props.footer && close) {
      if (!onlyOkButton) {
        result.push(
          <ModalButton
            key='cancel'
            actionFn={props.onCancel}
            close={close}
            buttonProps={props.cancelButtonProps}
          >
            {cancelText}
          </ModalButton>,
        );
      }
      result.push(
        <ModalButton
          key='ok'
          actionFn={props.onOk}
          close={close}
          buttonProps={props.okButtonProps || okButtonProps}
        >
          {okText}
        </ModalButton>,
      );
    }

    return result;
  }, [onlyOkButton, cancelText, props, close, okButtonProps, okText]);

  return (
    <StyledConfirmModal
      data-testid={`confirmModal-${modalType}-${size}`}
      okButtonProps={okButtonProps}
      cancelText={cancelText}
      okText={okText}
      {...props}
      {...(close && { onCancel: () => close({ triggerCancel: true }) })}
      closable={false}
      width={getModalWidthByType(modalType, size)}
      modalType={modalType}
      size={size}
      {...(!props.footer &&
        close && {
          footer,
        })}
    >
      <div className='bgEllipse'>
        <BgEllipse size={size} modalType={modalType} />
      </div>

      <img src={modalIconSrc} className='modalIcon' />

      <div className='text-container'>
        {title && <Title className='title'>{title}</Title>}

        {subTitle && (
          <Text color={colors.gray900} className='subTitle'>
            {subTitle}
          </Text>
        )}

        {content && <div className='content'>{content}</div>}

        {text && (
          <div className='text'>
            <Text color={colors.gray900}>{text}</Text>
          </div>
        )}
      </div>
    </StyledConfirmModal>
  );
};
